import {
  LeadSequenceReturnReadable,
  LeadSequenceStatus,
  leadsLogsListQueryKey,
  leadsSequencesListQueryKey,
  leadsSequencesPartialUpdateMutation
} from '@crm/api'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, Button, Dropdown, Flex, MenuProps } from 'antd'
import { ArrowDown2 } from 'iconsax-react'

import { useStyles } from './styles'

type Props = {
  leadId: string
  sequence: LeadSequenceReturnReadable
  bordered?: boolean
  block?: boolean
}

const StatusLabel = ({ name }: { name?: LeadSequenceStatus }) => {
  const { t } = useLocales()
  const { styles } = useStyles({ color: name })

  return (
    <Flex align="center" gap={8}>
      <div className={styles.statusDot} />

      {t(`campaigns.stats_${name}`)}
    </Flex>
  )
}

export const CampaignStatus = ({
  sequence,
  bordered,
  leadId,
  block
}: Props) => {
  const { t } = useLocales()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { notification } = App.useApp()

  const { mutate, isPending } = useMutation({
    ...leadsSequencesPartialUpdateMutation(),
    onError: (error) => showApiErrors(error),
    onSuccess: () => {
      invalidateQueries([
        leadsLogsListQueryKey({ path: { lead_id: leadId } }),
        leadsSequencesListQueryKey({ path: { lead_id: leadId } })
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const items: MenuProps['items'] = Object.values(LeadSequenceStatus).map(
    (name) => ({
      label: <StatusLabel name={name} />,
      key: name
    })
  )

  const onClickStatus = (status: LeadSequenceStatus) => {
    if (!sequence.sequence.id) return

    mutate({
      path: { lead_id: leadId, sequence: sequence.sequence.id },
      body: { status }
    })
  }

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items,
        selectedKeys: sequence?.status ? [sequence.status] : [],
        onClick: ({ key }) => onClickStatus(key as LeadSequenceStatus)
      }}
    >
      <Button
        loading={isPending}
        icon={<ArrowDown2 size={16} variant="Bold" />}
        iconPosition="end"
        type={bordered ? 'default' : 'text'}
        block={block}
      >
        <StatusLabel name={sequence?.status} />
      </Button>
    </Dropdown>
  )
}
